import React from 'react';
import {Table} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {accountCameFromItem, applyItemDefaults} from './ExportInvoiceModal.xeroLines';
import {formatTaxPercent} from './ExportDefaultTaxRate.display';
import {getProjectIndicatorString} from '../../../forecast-app/shared/components/project-indicator/support/ProjectIndicatorLogic';
import {XeroExportLineRow, XeroExportLinesTableProps, XeroTrackingCategory} from './XeroExportLinesTable.type';

/**
 * The invoice-line table for the Export to Xero modal, built to the finalised design.
 *
 * A new component rather than an extension of either existing line table. `invoice_entries_list`
 * has eight consumers and the Table block in `ExportInvoiceModal` also serves QuickBooks and
 * e-conomic; adding Xero-only columns to either would put this feature's blast radius across ten
 * unrelated screens.
 *
 * **Both modals render this under the flag**, and only under it - T4-132 built and tested the
 * component, T4-133 wired each modal's flag-on arm to it. The flag-OFF arms remain byte-identical
 * to `master`, which is what makes AC1 unconditional rather than argued.
 *
 * Purely props-driven, with no fetching of its own, so T4-133 supplies real data at the two call
 * sites without touching this file.
 */
const XeroExportLinesTable = ({
	rows,
	currency,
	accounts,
	items,
	trackingCategories,
	onRowsChange,
	idHeader = '',
	disabled,
	canRepriceLines = false,
	loading = false,
	failed = {accounts: false, items: false, trackingCategories: false},
}: XeroExportLinesTableProps): JSX.Element => {
	const {formatMessage, formatNumber} = useIntl();

	/*
	 * "Still loading", "could not load" and "nothing imported" are three different statements, and
	 * only the last is AC3's empty state. Saying it while a read is in flight, or after one
	 * failed, tells the user their configuration does not exist when it may be sitting there.
	 */
	const placeholder = (didFail: boolean, isEmpty: boolean, emptyId: string, selectId: string): string => {
		// Loading first: a read still in flight has not failed yet.
		if (loading) return formatMessage({id: 'common.loading'});
		if (didFail) return formatMessage({id: 'integrations.xero.line.load_failed'});
		return formatMessage({id: isEmpty ? emptyId : selectId});
	};

	/*
	 * Changes are applied to `rows`, the prop, not to the flattened `data` the table renders -
	 * so what leaves this component is the caller's own row shape, without the display-only
	 * fields added below.
	 */
	const updateRow = (target: XeroExportLineRow, changes: Partial<XeroExportLineRow>): void => {
		/*
		 * The guard lives here because it cannot live on the item. The design system's
		 * DropdownColumn.ListItem forwards only onClick and data-cy to DropdownList.Item and
		 * drops `disabled`, so passing it there protected nothing - the table stayed fully
		 * interactive while the modal was exporting.
		 */
		if (disabled) return;
		onRowsChange(rows.map(row => (row.id === target.id ? {...row, ...changes} : row)));
	};

	const itemLabel = (row: XeroExportLineRow): string => items.find(item => item.value === row.itemId)?.name ?? '';

	const accountLabel = (row: XeroExportLineRow): string => {
		const account = accounts.find(candidate => candidate.value === row.accountId);
		return account ? `${account.code} - ${account.name}` : '';
	};

	/*
	 * The SELECTED ACCOUNT's rate, which is the authoritative one - it is what the Account column
	 * labels itself from. Reading a rate copied onto the row instead meant that a row whose
	 * account was set elsewhere (`mapInvoiceEntryToRow` writes `xeroAccount` but never
	 * `xeroTaxRate`) showed the account and no rate, which is AC3 broken.
	 *
	 * A found account wins even when its rate is null: "this account has no rate" is an answer,
	 * and falling through to the row would show a rate the account does not carry. Only an id the
	 * list cannot resolve falls back.
	 */
	const rateFor = (row: XeroExportLineRow): number | null | undefined => {
		const account = accounts.find(candidate => candidate.value === row.accountId);
		return account ? account.taxRate : row.taxRate;
	};

	/*
	 * Through intl (formatTaxPercent), not interpolation: `${rate}%` prints 12.5% in every locale,
	 * where da and fr write 12,5 %. Its `maximumFractionDigits` is what PercentageColumn lacks - it
	 * rounds a real 12.5% Xero rate to 13%, stating a tax position the account does not carry.
	 *
	 * `== null` catches null as well as undefined, so an account with no rate and a row with none
	 * both render a dash. Zero is a real rate and stays distinguishable from no rate at all.
	 */
	const formatRate = (rate: number | null | undefined): string => (rate == null ? '-' : formatTaxPercent(formatNumber, rate));

	const trackingLabel =
		(trackingCategory: XeroTrackingCategory) =>
		(row: XeroExportLineRow): string => {
			const selected = row.tracking?.[trackingCategory.value];
			return trackingCategory.options.find(option => option.value === selected)?.name ?? '';
		};

	/*
	 * Keyed on the category id, never on the option name. Two categories can legitimately carry
	 * an option of the same name - "North" under both Region and Team - and keying on the label
	 * would make choosing one silently fill the other.
	 */
	const selectTracking = (row: XeroExportLineRow, categoryId: string, optionId: string | undefined): void => {
		// No `disabled` guard here: this only ever reaches the rows through updateRow, which
		// guards. Two guards for one invariant is one place to forget when the next writer lands.
		const tracking = {...(row.tracking ?? {})};
		if (optionId === undefined) {
			delete tracking[categoryId];
		} else {
			tracking[categoryId] = optionId;
		}
		updateRow(row, {tracking});
	};

	/*
	 * `project` is genuinely nullable - an invoice entry can exist without one - and the table's
	 * nested accessor warns on every such row ('"name" in deeply nested key "project.name"
	 * returned undefined'). Flattening it here keeps that noise out of the console without the
	 * component having to care why a line has no project.
	 */
	const data = rows.map(row => ({
		...row,
		projectName: row.project?.name ?? '',
		taxDisplay: formatRate(rateFor(row)),
	}));

	/*
	 * Children are built as one flat array rather than as nested JSX.
	 *
	 * `Table` types its children as `(JSX.Element | undefined)[] | JSX.Element` and
	 * `DropdownColumn` types its own as `ReactElement[]`, so mixing a single element with a
	 * `.map()` produces a nested array that does not typecheck - even though React.Children
	 * flattens it happily at runtime. Spreading keeps both the types and the runtime honest.
	 */
	const trackingColumns = trackingCategories.map(trackingCategory => (
		<Table.DropdownColumn
			key={`tracking-${trackingCategory.value}`}
			header={trackingCategory.name}
			label={trackingLabel(trackingCategory)}
			defaultLabel={formatMessage({id: 'common.select'}) + ' ' + trackingCategory.name}
			width={'160px'}
		>
			{/*
			 * No `disabled` on these items. DropdownColumn.ListItem forwards only onClick and
			 * data-cy to DropdownList.Item, so the prop was dropped and protected nothing - it is
			 * the prop that misled three review rounds. The real guard is in updateRow, which every
			 * write here passes through.
			 */}
			{[
				<Table.DropdownColumn.ListItem
					key={`tracking-none-${trackingCategory.value}`}
					onClick={(row: XeroExportLineRow) => selectTracking(row, trackingCategory.value, undefined)}
				>
					{formatMessage({id: 'integrations.xero.line.none'})}
				</Table.DropdownColumn.ListItem>,
				...trackingCategory.options.map(option => (
					<Table.DropdownColumn.ListItem
						key={`tracking-option-${option.value}`}
						onClick={(row: XeroExportLineRow) => selectTracking(row, trackingCategory.value, option.value)}
					>
						{option.name}
					</Table.DropdownColumn.ListItem>
				)),
			]}
		</Table.DropdownColumn>
	));

	return (
		<Table data={data} width={'100%'}>
			{[
				/*
				 * The same badge the flag-off table renders, built from the same two fields, so switching
				 * the flag does not change how a line identifies its project.
				 */
				<Table.ColoredIdIconColumn
					key={'id'}
					accessorKey={'customProjectId'}
					header={idHeader}
					renderProps={(row: XeroExportLineRow) => ({
						color: row.projectColor ?? '',
						/*
						 * Through the shared helper, not `customProjectId` alone. Only one of the two
						 * mappers feeding this table carries a custom id - the legacy modal's query
						 * never asked for one - so reading that field directly left every legacy line
						 * with a blank badge while the table beside it showed P22.
						 */
						text: getProjectIndicatorString(row.project?.companyProjectId, row.customProjectId),
					})}
				/>,
				<Table.TextColumn key={'project'} accessorKey={'projectName'} header={formatMessage({id: 'common.project'})} />,
				<Table.DropdownColumn
					key={'item'}
					header={formatMessage({id: 'integrations.xero.line.item'})}
					label={itemLabel}
					defaultLabel={placeholder(
						failed.items,
						items.length === 0,
						'integrations.xero.line.no_items',
						'integrations.xero.line.select_item'
					)}
					width={'180px'}
				>
					{[
						<Table.DropdownColumn.ListItem
							key={'item-none'}
							/*
							 * Through applyItemDefaults, not a literal: clearing the item has to put the
							 * ENTRY's price back, and the line's current price is the cleared product's.
							 * The account goes the same way when the product put it there - and stays
							 * when the user did, which is why the last argument is asked rather than
							 * assumed.
							 */
							onClick={(row: XeroExportLineRow) =>
								updateRow(
									row,
									applyItemDefaults(
										undefined,
										accounts,
										row.originalUnitPrice,
										accountCameFromItem(row, items)
									)
								)
							}
						>
							{formatMessage({id: 'integrations.xero.line.none'})}
						</Table.DropdownColumn.ListItem>,
						...items.map(item => (
							<Table.DropdownColumn.ListItem
								key={`item-${item.value}`}
								/*
								 * Picking an item also fills the account Xero holds for it, and the tax that
								 * account carries - what the user would otherwise copy across by hand. The
								 * account is a suggestion written to the same fields the Account column
								 * writes, so choosing a different one afterwards simply replaces it.
								 */
								/*
								 * The item is stripped of its price and description for a user who may not
								 * update the invoice, so the account and tax still fill while the line keeps
								 * its own figure and text. The resolver would reject such a body anyway; this
								 * is where neither is offered in the first place.
								 */
								onClick={(row: XeroExportLineRow) =>
									updateRow(
										row,
										applyItemDefaults(
											canRepriceLines
												? item
												: {...item, defaultUnitPrice: undefined, description: undefined},
											accounts,
											row.originalUnitPrice,
											/*
											 * The PREVIOUS product's account, read before this click replaces
											 * it. A product that names no account of its own still has to take
											 * the last one's with it, or the line keeps an account belonging
											 * to a product it no longer carries.
											 */
											accountCameFromItem(row, items)
										)
									)
								}
							>
								{item.name}
							</Table.DropdownColumn.ListItem>
						)),
					]}
				</Table.DropdownColumn>,
				<Table.TextColumn
					key={'description'}
					accessorKey={'entryDescription'}
					width={'232px'}
					header={formatMessage({id: 'common.description'})}
				/>,
				<Table.DropdownColumn
					key={'account'}
					header={formatMessage({id: 'integrations.xero.line.account'})}
					label={accountLabel}
					defaultLabel={placeholder(
						failed.accounts,
						accounts.length === 0,
						'integrations.xero.line.no_accounts',
						'integrations.xero.line.select_account'
					)}
					width={'200px'}
				>
					{[
						<Table.DropdownColumn.ListItem
							key={'account-none'}
							onClick={(row: XeroExportLineRow) =>
								updateRow(row, {
									accountId: undefined,
									accountCode: undefined,
									taxRate: undefined,
									taxType: undefined,
								})
							}
						>
							{formatMessage({id: 'integrations.xero.line.none'})}
						</Table.DropdownColumn.ListItem>,
						...accounts.map(account => (
							<Table.DropdownColumn.ListItem
								key={`account-${account.value}`}
								onClick={(row: XeroExportLineRow) =>
									updateRow(row, {
										accountId: account.value,
										accountCode: account.code,
										// null means the account carries no rate. Undefined keeps that
										// distinct from zero, which is a real rate some accounts have.
										taxRate: account.taxRate ?? undefined,
										// The rate is displayed; the TYPE is what the export sends,
										// because Xero's LineItem takes a TaxType and derives the
										// rate itself. Dropping it here left the modal showing one
										// rate while Xero booked the contact's default.
										taxType: account.taxType,
									})
								}
							>
								{`${account.code} - ${account.name}`}
							</Table.DropdownColumn.ListItem>
						)),
					]}
				</Table.DropdownColumn>,
				<Table.NumberColumn
					key={'quantity'}
					accessorKey={'quantity'}
					header={formatMessage({id: 'common.quantity'})}
				/>,
				<Table.CurrencyColumn
					key={'unitPrice'}
					currency={currency}
					accessorKey={'unitPrice'}
					header={formatMessage({id: 'common.unit_price'})}
					maximumFractionDigits={4}
				/>,
				<Table.PercentageColumn
					key={'discount'}
					accessorKey={'discountPercentage'}
					header={formatMessage({id: 'project_section.xero_modal_table_discount'})}
				/>,
				<Table.TextColumn key={'tax'} accessorKey={'taxDisplay'} header={formatMessage({id: 'common.tax'})} />,
				...trackingColumns,
				<Table.CurrencyColumn
					key={'totalPrice'}
					currency={currency}
					accessorKey={'totalPrice'}
					header={formatMessage({id: 'expense_item_modal.total_price'})}
				/>,
			]}
		</Table>
	);
};

export default XeroExportLinesTable;
